import React from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Link,
  Spinner,
} from '@spotoninc/nexus-react';
import {
  Box,
  Text,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Button as ChakraButton,
} from '@chakra-ui/react';
import { ArrowRightIcon } from '@spotoninc/nexus-web-assets';

/**
 * Add or Edit Privilege Modal Component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Object} props.selectedPrivilege - The privilege being edited (null for add mode)
 * @param {string} props.privilegeName - The name of the privilege
 * @param {Function} props.setPrivilegeName - Function to update the privilege name
 * @param {Array} props.integrations - List of available integrations
 * @param {Object} props.locations - Map of integration IDs to their locations
 * @param {Array} props.selectedIntegrations - List of selected integration IDs
 * @param {Function} props.handleIntegrationChange - Function to handle integration selection changes
 * @param {Function} props.handleOpenLocationModal - Function to open the location modal
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 * @param {Function} props.handleSubmit - Function to handle form submission
 * @param {Object} props.selectedLocations - Map of integration IDs to their selected locations
 */
const AddOrEditPrivilegeModal = ({
  isOpen,
  onClose,
  selectedPrivilege,
  privilegeName,
  setPrivilegeName,
  integrations,
  locations,
  selectedIntegrations,
  handleIntegrationChange,
  handleOpenLocationModal,
  isSubmitting,
  handleSubmit,
  selectedLocations,
}) => {
  // Function to determine location link text based on selection count
  const getLocationLinkText = integrationId => {
    if (selectedLocations?.[integrationId]?.length > 0) {
      const count = selectedLocations[integrationId].length;
      return `${count} ${count === 1 ? 'Location' : 'Locations'}`;
    }
    return 'Add locations';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={selectedPrivilege ? 'Edit Privilege' : 'Add Privilege'}
    >
      <ModalBody>
        <VStack spacing={4} align="center" px={3}>
          <FormControl isRequired>
            <FormLabel>Privilege Name</FormLabel>
            <Input
              value={privilegeName}
              onChange={e => setPrivilegeName(e.target.value)}
              placeholder="Enter privilege name"
            />
          </FormControl>

          <Box minWidth={{ base: '100%', md: '700px' }} px={{ base: 2, md: 6 }}>
            <Box
              maxHeight={{ base: 'auto', md: '400px' }}
              overflowY={{ base: 'visible', md: 'auto' }}
            >
              <Table>
                <TableHead
                  position="sticky"
                  top={0}
                  zIndex={1}
                  background="white"
                >
                  <TableRow variant="filled">
                    <TableHeaderCell>Integration Name</TableHeaderCell>
                    <TableHeaderCell
                      textAlign="center"
                      style={{ width: '200px' }}
                    >
                      Locations
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {integrations && integrations.length > 0 ? (
                    integrations.map(integration => (
                      <TableRow key={integration.id} variant="bottom-divider">
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Checkbox
                              id={`integration-${integration.id}`}
                              checked={selectedIntegrations.includes(
                                integration.id
                              )}
                              onChange={e =>
                                handleIntegrationChange(
                                  integration.id,
                                  e.target.checked
                                )
                              }
                              mr={3}
                            />
                            <Text>{integration.app_name}</Text>
                          </Box>
                        </TableCell>
                        <TableCell textAlign="center" width="150px">
                          <Link
                            color="blue.500"
                            onClick={() =>
                              handleOpenLocationModal(integration.id)
                            }
                            isDisabled={
                              !selectedIntegrations.includes(integration.id)
                            }
                            cursor="pointer"
                          >
                            {getLocationLinkText(integration.id)}
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} textAlign="center">
                        <Text py={4}>No integrations available</Text>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button variant="tonal" onClick={onClose}>
          Cancel
        </Button>
        <ChakraButton
          h="44px"
          colorScheme="blue"
          bg="#2d71e8"
          _hover={{ bg: '#1c5dcd' }}
          onClick={handleSubmit}
          isLoading={isSubmitting}
          borderRadius={100}
          px={3}
          // loadingText="Saving"
          ml={3}
          isDisabled={!privilegeName.trim() || !selectedIntegrations.length}
        >
          <Text px={3}>Save</Text>
        </ChakraButton>
      </ModalFooter>
    </Modal>
  );
};

export default AddOrEditPrivilegeModal;
